import React from "react";
import { toast } from "react-toastify";

import PassCollectModal from "components/Admin/PassCollectModal";
import RowMenu from "components/Admin/RowMenu";
import TableState from "components/Admin/TableState";
import { APIRequest, errorHandlerNew } from "utils/ApiRequest";
import { formatINR } from "utils/format";
import {
  COLLECTED,
  NEVER_COLLECTED,
  PENDING,
  STATUSES,
  collectionState,
  filterOrders,
  passLines,
  statusCounts,
} from "utils/passes";

const STATE_NAMES = { [PENDING]: "PENDING", [COLLECTED]: "COLLECTED", [NEVER_COLLECTED]: "NEVER_COLLECTED" };

/**
 * The whole pass-collection desk on one screen.
 *
 * It replaces three tabs — a search, an outstanding list and a collected list — that between them
 * queried a hardcoded fiscal year and decided what a "pass" was by matching code prefixes. The
 * server now answers with the current year's pass-bearing orders in one payload, so the filters
 * here are local and their counts are exact.
 */
function PassCollection({ token, viewOnly }) {
  const [data, setData] = React.useState(null);
  const [status, setStatus] = React.useState(PENDING);
  const [search, setSearch] = React.useState("");
  const [selected, setSelected] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(
    (options) => {
      if (token == null) return;
      APIRequest(token, "/getPassCollection", {}, (res) => setData(res.data), null, options);
    },
    [token]
  );

  React.useEffect(() => {
    load();
  }, [load]);

  const orders = data != null ? data.orders : null;
  // The rows and the status counts run through the same filter, so a badge cannot advertise rows
  // the table will not show.
  const shown = filterOrders(orders, { status, search });
  const counts = statusCounts(orders, { search });

  const handleSetState = (order, nextState) => {
    if (viewOnly) {
      toast.error("You have view only access and cannot perform this operation!");
      return;
    }
    setBusy(true);
    APIRequest(
      token,
      "/setPassCollectionState",
      { orderRcptId: order.orderRcptId, state: STATE_NAMES[nextState] },
      () => {
        setBusy(false);
        setSelected(null);
        // Reload rather than patching in place: the tiles, the chip counts and the status counts
        // all move together, and recomputing them by hand is how they drift apart.
        load();
      },
      (err) => {
        setBusy(false);
        errorHandlerNew(err);
      },
      { busy: "Updating collection…" }
    );
  };

  const rowActions = (order) => {
    const state = collectionState(order);
    const actions = [];
    if (state !== COLLECTED) {
      actions.push({
        key: "collected",
        label: "Mark collected",
        icon: "now-ui-icons ui-1_check",
        disabled: viewOnly,
        onSelect: () => handleSetState(order, COLLECTED),
      });
    }
    if (state === PENDING) {
      actions.push({
        key: "never",
        label: "Never collected",
        icon: "now-ui-icons ui-1_simple-remove",
        tone: "danger",
        disabled: viewOnly,
        onSelect: () => handleSetState(order, NEVER_COLLECTED),
      });
    } else {
      actions.push({
        key: "pending",
        label: "Move back to pending",
        icon: "now-ui-icons arrows-1_refresh-69",
        disabled: viewOnly,
        onSelect: () => handleSetState(order, PENDING),
      });
    }
    return actions;
  };

  return (
    <div className="hm-panel">
      <div className="hm-adm-panel-head">
        <div>
          <h2>Passes</h2>
          <p>Hand out passes and coupons, and keep the outstanding list honest.</p>
        </div>
        {data != null && <span className="hm-adm-fy">FY {data.fiscalYear}</span>}
      </div>

      {data != null && (
        <div className="hm-adm-tiles">
          <div className="hm-adm-tile">
            <span>Pending</span>
            <strong>{data.pendingOrders}</strong>
          </div>
          <div className="hm-adm-tile">
            <span>Collected</span>
            <strong>{data.collectedOrders}</strong>
          </div>
          <div className="hm-adm-tile">
            <span>Never collected</span>
            <strong>{data.neverCollectedOrders}</strong>
          </div>
          <div className="hm-adm-tile">
            <span>Passes out</span>
            <strong>{data.pendingPasses}</strong>
          </div>
        </div>
      )}

      <div className="hm-adm-search">
        <div className="hm-fld">
          <label className="hm-fld-l" htmlFor="adm-pass-search">Contributor</label>
          <div className="hm-fld-wrap">
            <input
              id="adm-pass-search"
              className="hm-fld-i"
              type="text"
              placeholder="Name, mobile, email or contribution ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="hm-seg" role="tablist" aria-label="Collection status">
        {STATUSES.map((option) => (
          <button
            key={option.id}
            className={status === option.id ? "is-on" : undefined}
            type="button"
            role="tab"
            aria-selected={status === option.id}
            onClick={() => setStatus(option.id)}
          >
            {option.label}
            <span className="hm-seg-n">{counts[option.id] || 0}</span>
          </button>
        ))}
      </div>

      {orders === null ? (
        <TableState icon="now-ui-icons arrows-1_refresh-69" title="Loading contributions…" />
      ) : shown.length === 0 ? (
        <TableState
          icon="now-ui-icons files_paper"
          title="Nothing here"
          message={
            search.trim() !== ""
              ? "No contribution matches that search in this list."
              : "No contribution is in this state for the current filters."
          }
        />
      ) : (
        <div className="hm-tbl-wrap">
          <table className="hm-tbl">
            <thead>
              <tr>
                <th className="is-name">Contributor</th>
                <th>Passes</th>
                <th className="is-num">Paid</th>
                <th className="is-act"></th>
              </tr>
            </thead>
            <tbody>
              {shown.map((order) => (
                <tr key={order.orderRcptId}>
                  <td className="is-name">
                    <span className="hm-tbl-strong">{order.userName}</span>
                    <span className="hm-tbl-sub is-id">{order.orderRcptId}</span>
                  </td>
                  <td>
                    {passLines(order).map((line) => (
                      <span key={line.code} className="hm-tag">
                        {line.name} ×{line.qty}
                      </span>
                    ))}
                  </td>
                  <td className="is-num">{formatINR(order.orderTotal)}</td>
                  <td className="is-act">
                    <div className="hm-btn-row">
                      <button
                        className="hm-btn hm-btn-outline hm-btn-sm"
                        type="button"
                        onClick={() => setSelected(order)}
                      >
                        <i className="now-ui-icons shopping_tag-content" aria-hidden="true"></i>
                        Collect passes
                      </button>
                      <RowMenu label={"More actions for " + order.userName} items={rowActions(order)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PassCollectModal
        order={selected}
        busy={busy}
        viewOnly={viewOnly}
        onClose={() => setSelected(null)}
        onSetState={handleSetState}
      />

      <div className="hm-tbl-foot">
        <button className="hm-btn hm-btn-outline" type="button" onClick={() => load({ busy: "Refreshing…" })}>
          Refresh
        </button>
        <span className="hm-tbl-count">
          {shown.length} of {orders != null ? orders.length : 0} contributions
        </span>
      </div>
    </div>
  );
}

export default PassCollection;
